import React, { useState, useEffect } from 'react';
import './MiningZoneSelector.css';
import '../Ship/ShipPurchaseDisplay.css';
import oreRockImg from '../../assets/rocks/ore-rock.png';
import iceRockIcon from '../../assets/rocks/ice-rock.png';
import lavaRockIcon from '../../assets/rocks/lava-rock.png';
import InfoPopover from '../InfoPopover';
import { useGameState } from '../../state/useGameState';
import { MiningZoneConstants } from '../../constants/miningZones';

// Import the type from gameState.ts
import type { MiningZoneDto } from '../../types/gameState';

// Map zone IDs to their icons
const zoneIcons: Record<string, string> = {
  [MiningZoneConstants.ORE_BELT]: oreRockImg,
  [MiningZoneConstants.ICE_BELT]: iceRockIcon,
  [MiningZoneConstants.LAVA_BELT]: lavaRockIcon,
};

interface MiningZoneSelectorProps {
  currentZoneId: string;
  playerId?: string; // Optional - kept for backward compatibility if needed elsewhere
  onZoneChange: (zoneId: string) => Promise<void>;
}

const ChevronIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <span
    className={"expand-icon" + (expanded ? " expanded" : "")}
    style={{ display: 'inline-block', transition: 'transform 0.3s', marginLeft: 8, transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
    aria-label={expanded ? 'Collapse' : 'Expand'}
  >
    <svg width="18" height="18" viewBox="0 0 20 20">
      <polyline points="6 8 10 12 14 8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

const MiningZoneSelector: React.FC<MiningZoneSelectorProps> = ({
  currentZoneId,
  onZoneChange
}) => {
  const { gameState } = useGameState(); // Get game state from context
  const [zones, setZones] = useState<MiningZoneDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<boolean>(true);
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [selectedZoneInfo, setSelectedZoneInfo] = useState<MiningZoneDto | null>(null);
  
  // Debug log current zone ID
  useEffect(() => {
    console.log('MiningZoneSelector received currentZoneId:', currentZoneId);
  }, [currentZoneId]);

  // Get mining zones from game state
  useEffect(() => {
    if (gameState) {
      if (Array.isArray(gameState.availableMiningZones) && gameState.availableMiningZones.length > 0) {
        setZones(gameState.availableMiningZones);
        setLoading(false);
        setError(null);
      } else {
        console.error('No mining zones in game state');
        setError('No mining zones available');
        setLoading(false);
      }
    }
  }, [gameState]);

  // Ensure the selector is expanded the first time zones load
  useEffect(() => {
    if (!hasAutoExpanded && zones.length > 0) {
      setExpanded(true);
      setHasAutoExpanded(true);
    }
  }, [zones.length, hasAutoExpanded]);

  if (loading && zones.length === 0) {
    return (
      <div className="ship-category-section size-small" style={{ flex: '1 1 100%' }}>
        <div className="category-header" style={{ cursor: 'pointer', boxSizing: 'border-box' }}>
          <h3>Mining Zones</h3>
          <ChevronIcon expanded={expanded} />
        </div>
        <div className="category-container">
          <div className="mining-zone-selector loading">Loading mining zones...</div>
        </div>
      </div>
    );
  }

  if (error && zones.length === 0) {
    return (
      <div className="ship-category-section size-small" style={{ flex: '1 1 100%' }}>
        <div className="category-header" style={{ cursor: 'pointer', boxSizing: 'border-box' }}>
          <h3>Mining Zones</h3>
          <ChevronIcon expanded={expanded} />
        </div>
        <div className="category-container">
          <div className="mining-zone-selector error">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="ship-category-section size-small" style={{ flex: '1 1 100%' }}>
      <div 
        className="category-header"
        style={{ cursor: 'pointer', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        onClick={() => setExpanded(!expanded)}
        tabIndex={0}
        role="button"
        aria-expanded={expanded}
      >
        <h3 style={{ margin: 0 }}>Mining Zones</h3>
        <ChevronIcon expanded={expanded} />
      </div>
      {expanded && (
        <div className="category-container">
          <div className="mining-zones-container ships-in-category" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
            {zones.map(zone => {
              // Case-insensitive comparison for more robust matching
              const isSelected = zone.id.toLowerCase() === (currentZoneId || '').toLowerCase();
              const icon = zoneIcons[zone.id] || zoneIcons[zone.id.toLowerCase()];
              
              // Build highlight class correctly
              let highlightClass = '';
              // Only add the unaffordable highlight if it's not selected
              if (!zone.canSelect && !isSelected) {
                highlightClass += ' highlight-unaffordable';
              }
              // Always add the selected class for selected zones
              if (isSelected) {
                highlightClass += ' selected';
              }
              
              return (
                <div
                  key={zone.id}
                  className={`ship-purchase-row upgrade-symbol-container${highlightClass}`}
                  onClick={() => zone.canSelect && onZoneChange(zone.id)}
                  title={!zone.canSelect ? "Recall all ships first" : `${zone.name} (ID: ${zone.id})`}
                  tabIndex={0}
                  role="button"
                  style={{
                    position: 'relative',
                    minWidth: 90,
                    maxWidth: 90,
                    width: 90, 
                    margin: '0.25rem',
                    borderRadius: 8,
                    opacity: !zone.canSelect && !isSelected ? 0.6 : 1,
                    background: 'var(--color-bg-tertiary, #232a3a)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                    border: !zone.canSelect 
                      ? '2px solid var(--color-border-error)'
                      : isSelected 
                        ? '2px solid var(--color-accent-blue, #2196f3)'
                        : '1px solid var(--color-border-light)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    padding: '0.25rem 0.25rem',
                    transition: 'box-shadow 0.2s, border 0.2s, transform 0.2s',
                    cursor: zone.canSelect ? 'pointer' : 'not-allowed'
                  }}
                >
                  <button 
                    className="ship-purchase-info-btn"
                    type="button"
                    onClick={e => {
                      e.stopPropagation(); // Prevent triggering zone selection
                      setPopoverAnchor(e.currentTarget);
                      setSelectedZoneInfo(zone);
                    }}
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      background: 'var(--color-bg-secondary, #1a2638)',
                      border: '1px solid var(--color-border-light, #334466)',
                      borderRadius: '50%',
                      width: 20,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 15,
                      padding: 0,
                      outline: 'none'
                    }}
                  >
                    <span style={{ fontSize: '0.95em', color: '#2196f3', fontWeight: 'bold', lineHeight: 1 }}>i</span>
                  </button>

                  {/* Title container with fixed height for alignment - matches ShipPurchaseDisplay */}
                  <div style={{ height: 40, display: 'flex', alignItems: 'center', marginBottom: '0.3rem', width: '100%' }}>
                    <div className={`ore-belt-name`} style={{ 
                      fontWeight: 'bold', 
                      fontSize: '0.9rem', 
                      textAlign: 'center', 
                      whiteSpace: 'normal', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      width: '100%', 
                      lineHeight: 1.15
                    }}>
                      {zone.name}
                    </div>
                  </div>

                  {/* Icon container with risk indicator */}
                  <div className="upgrade-symbol" style={{ 
                    position: 'relative', 
                    width: 50, 
                    height: 50, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    marginBottom: '0.3rem',
                    border: '1.5px solid',
                    borderColor: zone.riskLevel === 'High' 
                      ? 'var(--color-border-error, #e41f01)' 
                      : zone.riskLevel === 'Medium'
                        ? 'var(--color-warning, #ff9800)'
                        : 'var(--color-success, #4caf50)',
                    boxShadow: zone.riskLevel === 'High'
                      ? '0 0 8px rgba(228, 31, 1, 0.3)'
                      : zone.riskLevel === 'Medium'
                        ? '0 0 8px rgba(255, 152, 0, 0.3)'
                        : '0 0 8px rgba(76, 175, 80, 0.3)',
                  }}>
                    <img 
                      src={icon} 
                      alt={zone.name}
                      style={{ width: 45, height: 45, objectFit: 'contain' }}
                    />
                    {/* Risk badge */}
                    <div style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      background: zone.riskLevel === 'High' 
                        ? 'var(--color-text-error, #e41f01)' 
                        : zone.riskLevel === 'Medium'
                          ? 'var(--color-warning, #ff9800)'
                          : 'var(--color-success, #4caf50)',
                      color: 'white',
                      borderRadius: '50%',
                      width: 16,
                      height: 16,
                      fontSize: '0.6rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      border: '1px solid var(--color-bg-secondary, #1a2638)',
                      zIndex: 20,
                    }}>
                      {zone.riskLevel === 'High' ? 'H' : zone.riskLevel === 'Medium' ? 'M' : 'L'}
                    </div>
                  </div>

                  {/* Stats container */}
                  <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {/* Yield multiplier */}
                    <div style={{ 
                      fontWeight: 'bold', 
                      fontSize: '1.1rem', 
                      textAlign: 'center', 
                      width: '100%', 
                      color: 'var(--color-accent-blue, #2196f3)',
                      padding: '4px 0',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden'
                    }}>
                      +{(zone.miningYieldMultiplier * 100).toFixed(0)}%
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {selectedZoneInfo && popoverAnchor && (
        <InfoPopover
          anchorEl={popoverAnchor}
          onClose={() => {
            setSelectedZoneInfo(null);
            setPopoverAnchor(null);
          }}
          title={selectedZoneInfo.name || 'Mining Zone'}
          placement="bottom" // Ensure consistent placement for all popovers
        >
          <div style={{ minWidth: 240, maxWidth: 320 }}>
            {/* Description section */}
            <div style={{ marginBottom: '0.8rem', fontSize: '0.97em', opacity: 0.85 }}>
              {selectedZoneInfo.description}
            </div>
            
            {/* Specifications section header */}
            <div style={{ 
              fontSize: '0.97em', 
              fontWeight: 'bold', 
              borderBottom: '1px solid var(--color-border-light, #334466)', 
              paddingBottom: '4px', 
              marginBottom: '8px' 
            }}>
              Zone Specifications
            </div>
            
            {/* Table-like specifications */}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.97em' }}>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 600, padding: '4px 8px' }}>Risk Level</td>
                  <td style={{ 
                    textAlign: 'right', 
                    padding: '4px 8px'
                  }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontWeight: 'bold',
                      color: 'white',
                      background: selectedZoneInfo.riskLevel === 'High' 
                        ? 'var(--color-text-error, #e41f01)' 
                        : selectedZoneInfo.riskLevel === 'Medium'
                          ? 'var(--color-warning, #ff9800)'
                          : 'var(--color-success, #4caf50)'
                    }}>
                      {selectedZoneInfo.riskLevel}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600, padding: '4px 8px' }}>Yield Multiplier</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px' }}>
                    <span style={{ 
                      color: 'var(--color-accent-blue, #2196f3)',
                      fontWeight: 'bold'
                    }}>
                      +{((selectedZoneInfo.miningYieldMultiplier - 1) * 100).toFixed(0)}%
                    </span>
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600, padding: '4px 8px' }}>Warp Distance</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--color-accent-blue, #2196f3)' }}>
                    {selectedZoneInfo.warpDistance} AU
                  </td>
                </tr>
              </tbody>
            </table>
            
            {/* Notes section */}
            {selectedZoneInfo.notes && (
              <div style={{ fontSize: '0.9em', marginTop: '8px', padding: '6px', background: 'rgba(33, 150, 243, 0.1)', borderRadius: '4px' }}>
                <strong>Notes:</strong> {selectedZoneInfo.notes}
              </div>
            )}
            
            {/* Warning section for unavailable zones */}
            {!selectedZoneInfo.canSelect && (
              <div style={{ 
                marginTop: '12px', 
                color: 'var(--color-text-error, #e41f01)',
                background: 'rgba(228, 31, 1, 0.1)',
                padding: '8px',
                borderRadius: '4px',
                fontSize: '0.9em',
                display: 'flex',
                alignItems: 'center',
              }}>
                <span style={{ fontSize: '1.2em', marginRight: '6px' }}>⚠️</span>
                <span><strong>Warning:</strong> All ships must be recalled to the mothership before changing mining zones.</span>
              </div>
            )}
          </div>
        </InfoPopover>
      )}
    </div>
  );
};

export default MiningZoneSelector;
